import { Box, Button, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import PdfViewer from 'components/pdf-viewer';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { isEmpty, isNull } from 'underscore';
import { saveState } from 'utils/storage';
import OrderFeedTabsHeader from 'views/order-feed/components/order-feed-tabs-header';
import { OrderFeedProvider, useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { OrderFeedSettingsProvider } from 'views/order-feed/context/order-feed-settings-context';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { getOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/api/get-order';
import EditOrderDialog, {
  EditOrderDialogOrder,
} from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order/edit-order-dialog';
import { Order, SiloDistribution } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/order.type';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import {
  formatDate,
  getDefaultDispatchDate,
  getOrderDateValue,
  getSelectedTransportCompany,
  getSiteTransportCompanies,
} from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { OrderHistoryParams } from 'views/order-feed/features/order-history/types/order-history-params.type';
import { ChangeableStatus } from 'views/order-feed/types/changeable-status.enum';
import { OrderAttachment } from 'views/order-feed/types/order-attachment.type';
import { OrderStatus } from 'views/order-feed/types/order-status.enum';
import { DispatchSchedule } from 'views/order-feed/types/shipping-schedule.type';

const Line = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.lightGray.light}`,
  justifyContent: 'space-between',
  alignItems: 'flex-start',

  '&:nth-last-of-type(1)': {
    borderBottom: 'none',
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const Bold = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
}));

type Props = {};

const PAGE_NAME_ID = 'orderFeed.viewOrder';

//#TODO This should be updated with more robust logic when the Order type is updated by BE. It should include vessel / plantId to determine the schedule. For now, it uses warehouseName and the schedule's factory e.g. 'Bergneset' after filtering the schedule based on order date.
const getLegacyOrderSchedule = (order: Order, dispatchSchedule: DispatchSchedule | undefined) => {
  const schedulesForDate =
    dispatchSchedule?.dispatchDateData.filter(
      (schedule) => formatDate(schedule.dispatchDate) === formatDate(order.requestedDeliveryDate)
    ) ?? [];

  return (
    schedulesForDate.find((schedule) => schedule.factory === order.warehouseName) ??
    (schedulesForDate.length === 1 ? schedulesForDate[0] : undefined)
  );
};

const toEditOrderDialogOrder = (
  order: Order,
  dispatchSchedule: DispatchSchedule | undefined,
  customerErpId: string
): EditOrderDialogOrder => {
  const requestedDispatchDate = order.requestedDeliveryDate || order.orderLines[0]?.requestedDispatchDate || order.deliveryDate;
  const schedule = getLegacyOrderSchedule(order, dispatchSchedule);

  return {
    customerErpId,
    orderNumber: order.orderNumber,
    transportMethod: order.transportCompany,
    requestedDispatchDate: schedule ? getOrderDateValue(schedule) : requestedDispatchDate,
    siteErpId: order.siteErpId,
    orderLines: order.orderLines.map((line) => ({
      itemNumber: line.itemNumber,
      orderQuantity: line.orderQuantity,
    })),
    orderComment: order.orderComments || '',
    customerOrderReference: order.customersOrderNumber || '',
    plantId: schedule?.factoryId || '',
    vessel: schedule?.vessel || '',
    totalOrderQuantity: order.totalOrderQty,
    changeableStatus: order.changeableStatus,
  };
};

const ViewOrder: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const { account } = useAccountState();
  const params = useParams<OrderHistoryParams>();
  const {
    dispatchDatesQuery: { data: dispatchSchedule },
  } = useOrderFeedState();
  const {
    sitesQuery: { data: sites },
  } = useSitesState();
  const site = sites?.find((s) => s.globalSiteId === params.globalSiteId);
  const deliveryMethods = getSiteTransportCompanies(account.company?.addressAccess, params.globalSiteId);

  // #region STATE

  const defaultDispatchDate = getDefaultDispatchDate(dispatchSchedule);
  const [distributed, setDistributed] = useState<SiloDistribution[]>();

  // #endregion STATE

  // #region FETCH DATA

  const {
    data: order,
    error,
    isLoading,
    isError,
  } = useGet(
    ['getOrder', account.company?.customerErpId, params.orderNumber],
    ({ queryKey }) => getOrder(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.customerErpId && !!params.orderNumber,
    }
  );

  const editOrder = useMemo(
    () => (order ? toEditOrderDialogOrder(order, dispatchSchedule, String(account.company?.customerErpId || '')) : undefined),
    [account.company?.customerErpId, dispatchSchedule, order]
  );

  // #endregion FETCH DATA

  // #region HANDLERS

  const handleSaveOrderData = (data: CreateOrderFormValues) => {
    saveState({ key: 'createOrderData', state: data });
    history.push(`/order-feed/supply-plan/${params.siteErpId}/${params.globalSiteId}`);
  };

  const handleRowClick = (lineNumber: string) => {
    history.push(
      `/order-feed/order-history/${params.siteErpId}/${params.globalSiteId}/${params.orderNumber}/${lineNumber}?ascCompliant=${order?.ascCompliant}`
    );
  };
  // #endregion HANDLERS

  // #region RENDERS

  const renderNotApplicable = (value: string | number): string => {
    if (String(value) === '-1' || isNull(value) || isEmpty(value)) return intl.formatMessage({ id: 'general.notApplicable' });
    return String(value);
  };

  // #endregion RENDERS

  useEffect(() => {
    if (order?.orderDistributionHistory) {
      let distributionHistory = order.orderDistributionHistory.siloDistributions.map((element) => element);

      setDistributed(distributionHistory);
    }
  }, [order?.orderDistributionHistory]);

  // #region HELPERS

  const isChangeable = (status: ChangeableStatus) => {
    return [ChangeableStatus.WITH_CHARGE, ChangeableStatus.FOR_FREE].includes(status) && account.company?.features?.hasOrderFeed;
  };

  const hasRepeatOrder = () => {
    if (account.company?.features.hasOrderFreeDelivery && site?.isOfd1Enabled) return false; // hide if ofd is enabled at company AND site level
    return !!account.company?.features.hasOrderFeed; // else us hasOrderFeed as fallback
  };

  const orderDocuments = useMemo(() => {
    if (history.location.state) {
      return history.location.state as OrderAttachment;
    }
    return {} as OrderAttachment;
  }, [history]);

  const options: HeadCell[] = [
    {
      id: 'orderNumber',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderHistory.orderNumberLabel` }),
    },
    {
      id: 'itemName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderHistory.itemNameLabel` }),
    },
    {
      id: 'itemNumber',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderHistory.itemNumberLabel` }),
    },
    {
      id: 'siloName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderHistory.siloNameLabel` }),
    },
    {
      id: 'orderQuantity',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderHistory.quantityLabel` }),
      align: 'right',
    },
  ];
  // #endregion HELPERS

  return (
    <>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: String(site?.name),
                url: `${params.siteErpId}/${params.globalSiteId}`,
              },
              {
                text: order?.orderNumber || '',
                url: order?.orderNumber || '',
              },
            ]}
          />
        }
      />

      {isError ? (
        <Error error={error} />
      ) : !order || isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <FilterSidebar>
            {isChangeable(order.changeableStatus) && editOrder && <EditOrderDialog order={editOrder} />}
            {hasRepeatOrder() && (
              <Button
                size="small"
                onClick={() => {
                  const selectedTransportCompany = getSelectedTransportCompany(deliveryMethods, order.transportCompany);

                  handleSaveOrderData({
                    transportMethod: selectedTransportCompany?.key ?? '',
                    addressId: selectedTransportCompany?.name ?? '',
                    customerOrderReference: '',
                    orderComment: order.orderComments,
                    requestedDispatchDate: defaultDispatchDate || '',
                    siteErpId: order.siteErpId ?? '',
                    orderLines: order.orderLines.map((line) => ({
                      itemNumber: line.itemNumber,
                      orderQuantity: line.orderQuantity,
                    })),
                  });
                }}>
                <FormattedMessage id={`general.repeatOrder`} />
              </Button>
            )}
          </FilterSidebar>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Paper transparent>
              <Box mb={10}>
                <Typography variant="h3" color={theme.palette.common.white}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.orderLinesList.title`} />
                </Typography>
              </Box>
              <List
                options={[
                  { id: 'itemName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.itemNameLabel` }) },
                  { id: 'itemNumber', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.itemNumberLabel` }) },
                  {
                    id: 'requestedDispatchDate',
                    label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.requestedDispatchDateLabel` }),
                  },
                  {
                    id: 'orderQuantity',
                    label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderLinesList.orderQuantityLabel` }),
                    align: 'right',
                  },
                ]}>
                {order?.orderLines.map((item) => (
                  <ListRow onClick={() => handleRowClick(item.lineNumber)} key={item.lineNumber}>
                    <ListCell>{item.itemName}</ListCell>
                    <ListCell>{item.itemNumber}</ListCell>
                    <ListCell>
                      <FormattedDate value={item.requestedDispatchDate} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                    <ListCell align="right">
                      <FormattedNumber value={item.orderQuantity} style={`unit`} unit="kilogram" />
                    </ListCell>
                  </ListRow>
                ))}
              </List>
            </Paper>
            <Paper transparent>
              <Box mb={10}>
                <Typography variant="h3" color={theme.palette.common.white}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.title`} />
                </Typography>
              </Box>
              <Paper marginBottom={5}>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.transportCompanyNameLabel`} />
                  </Bold>
                  <Typography>{order?.transportCompanyName}</Typography>
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.deliveryDateLabel`} />
                  </Bold>
                  <FormattedDate value={order?.deliveryDate} day="2-digit" month="short" year="numeric" />
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.warehouseNameLabel`} />
                  </Bold>
                  <Typography>{order?.warehouseName}</Typography>
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.address1Label`} />
                  </Bold>
                  <Typography>{order?.address1}</Typography>
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.statusLabel`} />
                  </Bold>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.orderStatus.${order?.status}`} />
                  </Typography>
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.deliveryInfo.etaLabelLabel`} />
                  </Bold>
                  {order?.eta ? (
                    <Typography>
                      <FormattedDate
                        value={order.eta}
                        day="2-digit"
                        month="short"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        hour12={false}
                      />
                    </Typography>
                  ) : (
                    <FormattedMessage id="general.notApplicable" />
                  )}
                </Line>
              </Paper>
              <Box display={'flex'} alignItems={'flex-end'} justifyContent={'flex-end'}>
                {Number(orderDocuments.documentIds?.length) > 0 && (
                  <Dialog
                    maxWidth="md"
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.deliveryDocuments` })}
                    button={
                      <Button size="small" type="button">
                        <FormattedMessage id={`${PAGE_NAME_ID}.deliveryNotes`} />
                      </Button>
                    }>
                    <PdfViewer orderAttachment={orderDocuments} from={'order-history'} />
                  </Dialog>
                )}
              </Box>
            </Paper>
            <Paper transparent>
              <Box mb={10}>
                <Typography variant="h3" color={theme.palette.common.white}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.title`} />
                </Typography>
              </Box>
              <Paper marginBottom={5}>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.orderNumberLabel`} />
                  </Bold>
                  <Typography>#{order?.orderNumber}</Typography>
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.orderDateLabel`} />
                  </Bold>
                  <FormattedDate value={order?.orderDate} day="2-digit" month="short" year="numeric" />
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.requestedDeliveryDateLabel`} />
                  </Bold>
                  <FormattedDate value={order?.requestedDeliveryDate} day="2-digit" month="short" year="numeric" />
                </Line>
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.customersOrderNumberLabel`} />
                  </Bold>
                  <Typography>{renderNotApplicable(order?.customersOrderNumber)}</Typography>
                </Line>
                {order.ascCompliant && (
                  <Line>
                    <Bold>
                      <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.asc`} />
                    </Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.yes`} />
                  </Line>
                )}
                <Line>
                  <Bold>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderInfo.orderCommentsLabel`} />
                  </Bold>
                  <Typography>{order?.orderComments}</Typography>
                </Line>
              </Paper>
            </Paper>

            {order.status === OrderStatus.Invoiced && (
              <>
                <Paper transparent>
                  <Box mb={10}>
                    <Typography variant="h3" color={theme.palette.common.white}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.invoiceInfo.title`} />
                    </Typography>
                  </Box>
                  <List
                    sx={{ mb: 5 }}
                    options={[
                      { id: 'itemNumber', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderDeliveriesList.itemNumberLabel` }) },
                      {
                        id: 'orderQuantity',
                        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderDeliveriesList.orderQuantityLabel` }),
                        align: 'right',
                      },
                    ]}>
                    {order?.orderDeliveries.map((item) => (
                      <ListRow key={item.lineNumber}>
                        <ListCell>{item.itemNumber}</ListCell>
                        <ListCell align="right">
                          <FormattedNumber value={item.invoicedQuantity} style={`unit`} unit="kilogram" />
                        </ListCell>
                      </ListRow>
                    ))}
                  </List>
                  <Paper>
                    <Line>
                      <Bold>
                        <FormattedMessage id={`${PAGE_NAME_ID}.invoiceInfo.customerNameLabel`} />
                      </Bold>
                      <Typography>
                        <FormattedMessage id="general.notApplicable" />
                      </Typography>
                    </Line>
                    <Line>
                      <Bold>
                        <FormattedMessage id={`${PAGE_NAME_ID}.invoiceInfo.customerNoLabel`} />
                      </Bold>
                      <Typography>
                        <FormattedMessage id="general.notApplicable" />
                      </Typography>
                    </Line>
                    <Line>
                      <Bold>
                        <FormattedMessage id={`${PAGE_NAME_ID}.invoiceInfo.invoiceNumberLabel`} />
                      </Bold>
                      <Typography>{order.invoiceNumber}</Typography>
                    </Line>
                  </Paper>
                </Paper>
              </>
            )}
            {order.orderDistributionHistory && (
              <Paper transparent>
                <Box mb={10}>
                  <Typography variant="h3" color={theme.palette.common.white}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.orderHistory.title`} />
                  </Typography>
                </Box>
                <Paper>
                  <Line>
                    <Bold>
                      <FormattedMessage id={`${PAGE_NAME_ID}.orderHistory.orderNumberLabel`} />
                    </Bold>
                    <Typography>#{order?.orderDistributionHistory.orderNumber}</Typography>
                  </Line>
                  <Line>
                    <Bold>
                      <FormattedMessage id={`${PAGE_NAME_ID}.orderHistory.updatedAtLabel`} />
                    </Bold>
                    <FormattedDate value={order?.orderDistributionHistory.updatedAt} day="2-digit" month="short" year="numeric" />
                  </Line>
                  <Line>
                    <Bold>
                      <FormattedMessage id={`${PAGE_NAME_ID}.orderHistory.updatedByLabel`} />
                    </Bold>
                    <Typography>{order.orderDistributionHistory.updatedBy}</Typography>
                  </Line>
                </Paper>
                <List
                  options={options}
                  sort={setDistributed}
                  noResult={{
                    show: !distributed?.length,
                    text: intl.formatMessage({ id: `general.noResults` }),
                  }}>
                  {distributed?.map((distribution, idx) => {
                    return (
                      <ListRow key={idx}>
                        <ListCell>{distribution.orderNumber}</ListCell>
                        <ListCell>{distribution.itemName}</ListCell>
                        <ListCell>{distribution.itemNumber}</ListCell>
                        <ListCell>{distribution.siloName}</ListCell>
                        <ListCell align="right">
                          <FormattedNumber
                            maximumFractionDigits={2}
                            value={distribution.quantity || 0}
                            style={`unit`}
                            unit="kilogram"
                          />
                        </ListCell>
                      </ListRow>
                    );
                  })}
                </List>
              </Paper>
            )}
          </Box>
        </>
      )}
    </>
  );
};

const ViewOrderContainer: React.FC<Props> = () => (
  <OrderFeedProvider>
    <OrderFeedSettingsProvider>
      <ViewOrder />
    </OrderFeedSettingsProvider>
  </OrderFeedProvider>
);

export default ViewOrderContainer;
